import clsx from 'clsx';
import { ReactElement, cloneElement, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../lib/redux/hooks';
import { colors } from '../../colors';
import { ChevronDownIcon } from '../../icons/chevronDown';
import { ChevronrightIcon } from '../../icons/chevronRight';
import { MenuStatus } from '../../layout/dto';
import { MenuItems, MenuItemsActivePathsMap, MenuItemsHrefMap } from './dto';

interface SubMenuItem {
  label: string;
  link: string;
}

interface Props {
  link: {
    name: string,
    href: string,
    icon: ReactElement
  },
  subMenu?: boolean
  subMenuItems?: SubMenuItem[]
}

function SubMenuLink({ item }: { item: SubMenuItem }) {
  const [hover, setHover] = useState(false)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isActive = pathname === item.link

  return (
    <button
      type="button"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => navigate(item.link)}
      className={clsx(
        'grid w-full grid-cols-[18px,1fr] items-center gap-2 rounded-md px-2 py-2 text-left transition-colors text-sm font-medium cursor-pointer',
        {
          'text-brandPrimary-50 bg-brandPrimary-700': isActive,
          'text-brandPrimary-50 bg-white/10': !isActive && hover,
          'text-brandPrimary-200': !isActive && !hover,
        }
      )}
    >
      <span className="flex h-[18px] w-[18px] items-center justify-center">
        <ChevronrightIcon size={18} color={isActive || hover ? colors.brandPrimary[50] : colors.brandPrimary[200]} />
      </span>
      <span className="text-left leading-snug">
        {item.label}
      </span>
    </button>
  )
}

export default function NavLinks(props: Props) {

  const { link, subMenu, subMenuItems } = props
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const linkIcon = link.icon;
  const [hover, setHover] = useState(false)

  const currentPath = pathname.split('/').filter((_, index) => index !== 0)[0] || '';
  
  let isActive = link.href.includes(currentPath) && currentPath !== '';
  
  let currentMenuItem: MenuItems | undefined;
  Array.from(MenuItemsHrefMap.entries()).forEach(([key, val]) => {
    if (val === link.href) {
      currentMenuItem = key;
    }
  });

  if (currentMenuItem) {
    const activePaths = MenuItemsActivePathsMap.get(currentMenuItem) || [];
    if (activePaths.includes(currentPath) || (currentPath === '' && link.href === '/')) {
      isActive = true;
    }
  }

  const hasActiveChild = subMenuItems?.some(item => pathname === item.link) ?? false
  const isMenuActive = hasActiveChild || isActive
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(hasActiveChild)

  useEffect(() => {
    if (isMenuActive) {
      setIsSubMenuOpen(true)
    }
  }, [isMenuActive])

  const layoutState = useAppSelector(state => state.layout)

  const getIconColor = (isHovered: boolean) => {
    if (isActive || isHovered) return colors.brandPrimary[50]
    return colors.brandPrimary[200]
  }

  if (subMenu) {
    return (
      <div className="w-full">
        <span
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onClick={() => setIsSubMenuOpen(prev => !prev)}
          className={clsx(
            "flex h-[40px] w-full items-center rounded-[8px] p-[8px] border-1 cursor-pointer focus:outline-none focus:shadow-xs hover:shadow-xs text-sm font-medium transition-colors",
            {
              'bg-brandPrimary-700 text-brandPrimary-50': isMenuActive,
              'text-brandPrimary-200 hover:text-brandPrimary-50 hover:bg-white/10': !isMenuActive,
              'justify-center': layoutState.menuStatus === MenuStatus.CLOSED,
              'justify-between': layoutState.menuStatus !== MenuStatus.CLOSED,
            }
          )}
        >
          <span className="flex items-start gap-2">
            {cloneElement(linkIcon, { color: isMenuActive || hover ? colors.brandPrimary[50] : colors.brandPrimary[200] })}
            {layoutState.menuStatus !== MenuStatus.CLOSED &&
              <p className="text-label-sm text-left leading-snug font-medium">{link.name}</p>
            }
          </span>
          {layoutState.menuStatus !== MenuStatus.CLOSED && (
            <span className={clsx('transition-transform duration-200', {
              'rotate-0': isSubMenuOpen,
              '-rotate-90': !isSubMenuOpen,
            })}>
              <ChevronDownIcon size={18} color={isMenuActive || hover ? colors.brandPrimary[50] : colors.brandPrimary[200]} />
            </span>
          )}
        </span>

        {isSubMenuOpen && layoutState.menuStatus !== MenuStatus.CLOSED && (
          <div className="pl-4 flex flex-col gap-1">
            {subMenuItems?.map((item, index) => (
              <SubMenuLink key={index} item={item} />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <span
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => navigate(link.href)}
      className={clsx(
        "flex h-[40px] w-full items-center rounded-[8px] p-[8px] border-1 cursor-pointer focus:outline-none focus:shadow-xs hover:shadow-xs text-sm font-medium transition-colors",
        {
          'bg-brandPrimary-700 text-brandPrimary-50': isActive,
          'text-brandPrimary-200 hover:text-brandPrimary-50 hover:bg-white/10': !isActive,
          'justify-center': layoutState.menuStatus === MenuStatus.CLOSED,
          'justify-between': layoutState.menuStatus !== MenuStatus.CLOSED,
        }
      )}
    >
      <div className="flex flex-row justify-center items-start gap-2">
        {cloneElement(linkIcon, { color: getIconColor(hover) })}
        {layoutState.menuStatus !== MenuStatus.CLOSED &&
          <p className="text-label-sm text-left leading-snug font-medium">{link.name}</p>
        }
      </div>
    </span>
  )
}